import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Card, Divider, Tooltip, Typography} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";
import ConfigurableInfoBlock, {ConfigurableInfoBlockInputTypes} from "../../../../../../../components/app-specific/configurable-info-block";
import {FreightProvider} from "../../../../../../../../core/constants/enums";
import Api from "../../../../../../../../core/services/api/api";

export const FreightSettingsConfigurationsSection = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settings.organizational.freight-settings'});
    const [loading, setLoading] = useState(true);
    const [provider, setProvider] = useState(FreightProvider.Local);
    const isMounted = useIsMounted();

    const providerOptions = useMemo(() => [
        {value: FreightProvider.Local, label: t('providers.local'), id: FreightProvider.Local},
        {value: FreightProvider.FreightCOM, label: t('providers.freightcom'), id: FreightProvider.FreightCOM},
    ], [t]);

    /**
     * Fetches the freight quote estimation provider configuration from the server.
     */
    const getConfigurations = useCallback(async () => {
        setLoading(true);
        const response = await Api.getQuoteProvider();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setProvider(response.data?.serviceName ?? FreightProvider.Local);
        }
    }, [isMounted]);

    /**
     * Loads freight quote estimation provider configuration on mount.
     */
    useEffect(() => {
        void getConfigurations();
    }, [getConfigurations]);

    /**
     * Saves the selected freight quote estimation provider.
     *
     * @type {function(string): Promise<ApiResponseDs|{resultFlag: boolean}>}
     */
    const updateQuoteProvider = useCallback(async (serviceName) => {
        if (serviceName === provider) {
            return {resultFlag: true};
        }
        const response = await Api.saveQuoteProvider({serviceName});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProvider(response.data?.serviceName ?? serviceName);
        }
        return response;
    }, [isMounted, provider]);

    return (
        <>
            {
                loading &&
                <Grid2 xs={12}>
                    <div className={'loading access-level w-100'}>
                        <div>
                            <div/>
                        </div>
                    </div>
                </Grid2>
            }
            {
                !loading &&
                <Grid2 xs={12}>
                    <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12}>
                                <Typography variant={'subtitle1'} fontWeight={600}>
                                    {t('title')}
                                </Typography>
                            </Grid2>
                            <Grid2 xs={12}>
                                <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                            </Grid2>
                            <Grid2 xs={12} paddingInline={3}>
                                <ConfigurableInfoBlock
                                    title={t('providerLabel')}
                                    inputType={ConfigurableInfoBlockInputTypes.select}
                                    inputProps={{
                                        value: provider,
                                        data: providerOptions,
                                        SelectProps: {
                                            renderValue: (selectedValue) => (
                                                <p>{providerOptions.find(option => option.value === selectedValue)?.label ?? selectedValue ?? ''}</p>
                                            ),
                                        },
                                    }}
                                    onValueChanged={updateQuoteProvider}
                                    showCustomIcons
                                    customIcons={[
                                        <Tooltip key={1} title={t('providerTooltip')}>
                                            <InfoOutlined color={'primary'}/>
                                        </Tooltip>,
                                    ]}
                                />
                            </Grid2>
                        </Grid2>
                    </Card>
                </Grid2>
            }
        </>
    );
};
